import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Receipt, RefreshCw, Loader2, CheckCircle2, XCircle, AlertCircle,
  Users, Hash, DollarSign, Play, Building2,
} from 'lucide-react';
import { toast } from 'sonner';

const fmtMoney = (n) =>
  typeof n === 'number'
    ? `$${n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—';

const fmtFecha = (s) => {
  if (!s) return '—';
  const d = String(s).slice(0, 10);
  const [y, m, day] = d.split('-');
  return y && m && day ? `${day}/${m}/${y}` : d;
};

const ProformasPage = () => {
  const { token } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const [facturas, setFacturas] = useState([]);
  const [sel, setSel] = useState({});          // fact_id -> { selected, modo, importe }
  const [cargando, setCargando] = useState(false);
  const [creando, setCreando] = useState(false);
  const [error, setError] = useState(null);
  const [resultados, setResultados] = useState(null);

  // ── carga de facturas ──
  const cargar = useCallback(async () => {
    setCargando(true); setError(null); setResultados(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/proformas/facturas`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
      const fx = data.facturas || [];
      setFacturas(fx);
      const ini = {};
      fx.forEach((f) => {
        ini[f.fact_id] = { selected: false, modo: 'total', importe: f.pendiente ?? 0 };
      });
      setSel(ini);
      toast.success(`${fx.length} factura(s) cargada(s).`);
    } catch (err) {
      setError(err.message);
      toast.error('Error al cargar facturas: ' + err.message);
    } finally {
      setCargando(false);
    }
  }, [API_URL, token]);

  useEffect(() => { cargar(); }, [cargar]);

  // ── selección / edición ──
  const setRow = (id, patch) =>
    setSel((p) => ({ ...p, [id]: { ...p[id], ...patch } }));

  const toggleRow = (id) => setRow(id, { selected: !sel[id]?.selected });

  const setModo = (f, modo) => {
    if (modo === 'total') setRow(f.fact_id, { modo, importe: f.pendiente ?? 0 });
    else setRow(f.fact_id, { modo });
  };

  const setImporte = (f, val) => {
    const max = f.pendiente ?? 0;
    let n = val === '' ? '' : Number(val);
    if (typeof n === 'number' && n > max) n = max;
    setRow(f.fact_id, { importe: n, selected: true });
  };

  const allSelected = facturas.length > 0 && facturas.every((f) => sel[f.fact_id]?.selected);
  const toggleAll = () => {
    const next = !allSelected;
    setSel((p) => {
      const c = { ...p };
      facturas.forEach((f) => { c[f.fact_id] = { ...c[f.fact_id], selected: next }; });
      return c;
    });
  };

  // ── resumen ──
  const seleccionadas = useMemo(
    () => facturas.filter((f) => sel[f.fact_id]?.selected),
    [facturas, sel]);

  const totalImputar = useMemo(
    () => seleccionadas.reduce((a, f) => a + (Number(sel[f.fact_id]?.importe) || 0), 0),
    [seleccionadas, sel]);

  const clientes = useMemo(
    () => new Set(seleccionadas.map((f) => f.cliente_codigo)).size,
    [seleccionadas]);

  // ── crear + emitir ──
  const crear = async () => {
    if (!seleccionadas.length) { toast.info('Seleccioná al menos una factura.'); return; }
    const invalidas = seleccionadas.filter((f) => !(Number(sel[f.fact_id]?.importe) > 0));
    if (invalidas.length) { toast.error('Hay facturas seleccionadas con importe 0.'); return; }

    setCreando(true); setError(null); setResultados(null);
    try {
      const payload = {
        facturas: seleccionadas.map((f) => ({
          fact_id: f.fact_id,
          cliente_codigo: f.cliente_codigo,
          cliente_nombre: f.cliente_nombre,
          importe: Number(sel[f.fact_id].importe),
        })),
      };
      const res = await fetch(`${API_URL}/api/admin/proformas/crear`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
      setResultados(data);
      const { creados = 0, emitidos = 0, con_error = 0 } = data;
      if (con_error) toast.warning(`${creados} recibo(s) creado(s), ${emitidos} emitido(s), ${con_error} con error.`);
      else toast.success(`${creados} recibo(s) creado(s) y ${emitidos} emitido(s).`);
    } catch (err) {
      setError(err.message);
      toast.error('Error al crear recibos: ' + err.message);
    } finally {
      setCreando(false);
    }
  };

  return (
    <div className="text-gray-100">
      <div className="max-w-6xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 bg-[#1A1D27] p-6 rounded-xl border border-gray-800 shadow-xl">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-[#00a1df] to-blue-400 bg-clip-text text-transparent flex items-center gap-3">
              <Receipt className="w-7 h-7 text-[#00a1df]" />
              Proformas
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Seleccioná facturas y generá un recibo de cobro en efectivo por cliente. Los recibos se crean y se emiten automáticamente.
            </p>
          </div>
          <button
            onClick={cargar}
            disabled={cargando}
            className="flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 text-white px-4 py-2.5 rounded-lg shadow-lg transition-all font-black text-xs active:scale-95 whitespace-nowrap"
          >
            {cargando ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {cargando ? 'CARGANDO…' : 'ACTUALIZAR'}
          </button>
        </div>

        {error && (
          <div className="bg-red-900/20 border border-red-500/50 rounded-xl p-5 flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-red-500 shrink-0" />
            <p className="text-red-300/90 text-sm">{error}</p>
          </div>
        )}

        {/* Stats + acción */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="grid grid-cols-3 gap-4 flex-1">
            <StatCard label="Seleccionadas" value={seleccionadas.length} icon={<Hash className="w-5 h-5" />} color="text-[#00a1df]" bg="bg-[#00a1df]/10" />
            <StatCard label="Total a imputar" value={fmtMoney(totalImputar)} icon={<DollarSign className="w-5 h-5" />} color="text-green-400" bg="bg-green-500/10" />
            <StatCard label="Recibos (clientes)" value={clientes} icon={<Users className="w-5 h-5" />} color="text-amber-400" bg="bg-amber-500/10" />
          </div>
          <button
            onClick={crear}
            disabled={creando || !seleccionadas.length}
            className="flex items-center justify-center gap-2 bg-[#00a1df] hover:bg-[#0090c7] disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed text-white px-5 py-2.5 rounded-lg shadow-lg transition-all font-black text-xs active:scale-95 whitespace-nowrap"
          >
            {creando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4 fill-current" />}
            {creando ? 'CREANDO…' : 'CREAR Y EMITIR RECIBOS'}
          </button>
        </div>

        {/* Resultados */}
        {resultados && (
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-5 space-y-2 shadow-xl">
            <p className="text-sm font-bold text-white flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-400" />
              {resultados.creados} recibo(s) creado(s) · {resultados.emitidos} emitido(s)
              {resultados.con_error ? ` · ${resultados.con_error} con error` : ''}
            </p>
            <div className="space-y-1.5">
              {(resultados.resultados || []).map((r, i) => (
                <div key={i} className={`text-[11px] rounded-lg px-3 py-2 border flex items-start gap-2 ${
                  r.ok ? 'bg-green-900/15 border-green-500/30 text-green-200'
                       : 'bg-red-900/15 border-red-500/30 text-red-200'}`}>
                  {r.ok ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5" /> : <XCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />}
                  <span>
                    <span className="font-bold">{r.cliente_nombre || `Cliente ${r.clie_id || ''}`}</span>
                    {r.ok ? (
                      <> — Recibo #{r.recibo_id} · {fmtMoney(r.total_imputado)} · {r.fact_ids?.length} factura(s)
                        {r.emitido
                          ? <span className="text-green-400 font-bold"> · Emitido ✓</span>
                          : <span className="text-amber-400 font-bold"> · Creado pero NO emitido{r.emision_error ? `: ${r.emision_error}` : ''}</span>}
                      </>
                    ) : (
                      <> — {r.error}</>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabla de facturas */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden shadow-xl">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-900/60 text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="py-3 px-3 w-10 text-center">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} className="accent-[#00a1df] w-4 h-4 cursor-pointer" />
                  </th>
                  <th className="text-left py-3 px-2 font-bold">Cliente</th>
                  <th className="text-left py-3 px-2 font-bold w-20">Pedido</th>
                  <th className="text-left py-3 px-2 font-bold w-24">Emisión</th>
                  <th className="text-right py-3 px-2 font-bold w-32">Pendiente</th>
                  <th className="text-right py-3 px-2 font-bold w-32">Total fact.</th>
                  <th className="text-center py-3 px-2 font-bold w-40">Modo</th>
                  <th className="text-right py-3 px-2 font-bold w-36">A imputar</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {cargando && (
                  <tr><td colSpan={8} className="text-center text-gray-500 py-8">
                    <Loader2 className="w-5 h-5 animate-spin inline" /> Cargando facturas…
                  </td></tr>
                )}
                {!cargando && !facturas.length && (
                  <tr><td colSpan={8} className="text-center text-gray-600 py-8 text-[11px]">No hay facturas pendientes.</td></tr>
                )}
                {facturas.map((f) => {
                  const row = sel[f.fact_id] || {};
                  const isSel = !!row.selected;
                  return (
                    <tr key={f.fact_id} className={isSel ? 'bg-[#00a1df]/5' : 'hover:bg-gray-800/40'}>
                      <td className="py-2 px-3 text-center">
                        <input type="checkbox" checked={isSel} onChange={() => toggleRow(f.fact_id)} className="accent-[#00a1df] w-4 h-4 cursor-pointer" />
                      </td>
                      <td className="py-2 px-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <Building2 className="w-3.5 h-3.5 text-[#00a1df]/70 shrink-0" />
                          <span className="truncate text-gray-200">{f.cliente_nombre || '—'}</span>
                        </div>
                        <span className="text-[10px] text-gray-600 font-mono">{f.cliente_codigo}</span>
                      </td>
                      <td className="py-2 px-2 text-gray-400 font-mono">{f.pedido || '—'}</td>
                      <td className="py-2 px-2 text-gray-400">{fmtFecha(f.fecha_emision)}</td>
                      <td className="py-2 px-2 text-right text-amber-300 font-mono">{fmtMoney(f.pendiente)}</td>
                      <td className="py-2 px-2 text-right text-gray-400 font-mono">{fmtMoney(f.total)}</td>
                      <td className="py-2 px-2">
                        <div className="flex items-center justify-center gap-1">
                          <ModoBtn active={row.modo === 'total'} onClick={() => setModo(f, 'total')}>Total</ModoBtn>
                          <ModoBtn active={row.modo === 'parcial'} onClick={() => setModo(f, 'parcial')}>Parcial</ModoBtn>
                        </div>
                      </td>
                      <td className="py-2 px-2 text-right">
                        {row.modo === 'parcial' ? (
                          <input
                            type="number"
                            min="0"
                            max={f.pendiente ?? 0}
                            value={row.importe ?? ''}
                            onChange={(e) => setImporte(f, e.target.value)}
                            className="w-32 bg-gray-900 border border-gray-600 rounded-md px-2 py-1 text-xs text-white text-right focus:border-[#00a1df] focus:outline-none"
                          />
                        ) : (
                          <span className="text-white font-mono font-bold">{fmtMoney(f.pendiente)}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const ModoBtn = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-2 py-1 rounded text-[10px] font-bold transition-colors ${
      active ? 'bg-[#00a1df] text-white' : 'bg-gray-900 text-gray-400 hover:bg-gray-700'}`}
  >
    {children}
  </button>
);

const StatCard = ({ label, value, icon, color, bg }) => (
  <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 flex items-center gap-3 shadow-lg">
    <div className={`p-2 rounded-lg ${bg} ${color}`}>{icon}</div>
    <div className="min-w-0">
      <p className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">{label}</p>
      <p className="text-lg font-black text-white truncate">{value}</p>
    </div>
  </div>
);

export default ProformasPage;
